import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiFileText, FiFile, FiCalendar, FiLayers, FiShield, FiBriefcase, FiUser, FiMail,
  FiPhone, FiMapPin, FiClock, FiDollarSign, FiTag, FiGrid,
  FiChevronDown, FiCheck, FiX, FiHash, FiAlertCircle, FiLock,
} from 'react-icons/fi';
import { contractsAPI } from '../../services/api';
import { sanitizeFieldSchema } from '../../utils/sanitizeSchema';
import { formatDateMedium } from '../../utils/dateFormat';
import { formatMoney, formatNumber } from '../../utils/numberFormat';
import { getLocalized } from '../../i18n/getLocalized';
import useCurrencyNames from '../../hooks/useCurrencyNames';
import './ContractOverview.scss';

const PRIORITY_ORDER = ['low', 'medium', 'high', 'critical'];
const PRIORITY_COLOR = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#dc2626',
};

const CONFIDENTIALITY_ORDER = ['public', 'internal', 'confidential', 'restricted'];
const CONFIDENTIALITY_COLOR = {
  public: '#10b981',
  internal: '#3b82f6',
  confidential: '#f59e0b',
  restricted: '#dc2626',
};

/** One read-only field — same label + control footprint as the creation form. */
function ViewField({ label, icon: Icon, full, multiline, children, isEmpty }) {
  return (
    <div className={`cov-field${full ? ' cov-field--full' : ''}`}>
      <label className="cov-field__label">
        {Icon && <Icon size={13} />}
        {label}
      </label>
      <div
        className={`cov-value${isEmpty ? ' cov-value--empty' : ''}${multiline ? ' cov-value--multiline' : ''}`}
      >
        {isEmpty ? '—' : children}
      </div>
    </div>
  );
}

/** Collapsible section shell mirroring the creation form's `cf-section`. */
function ViewSection({ title, subtitle, icon: Icon, color, badge, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const accent = color || '#ef4444';
  return (
    <div className="cov-section" style={{ '--accent': accent, '--accent-bg': `${accent}14` }}>
      <button
        type="button"
        className={`cov-section__header${open ? ' cov-section__header--expanded' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="cov-section__header-left">
          <span className="cov-section__header-icon">{Icon && <Icon size={17} />}</span>
          <span className="cov-section__header-text">
            <span className="cov-section__header-title">{title}</span>
            {subtitle && <span className="cov-section__header-subtitle">{subtitle}</span>}
          </span>
        </span>
        <span className="cov-section__header-right">
          {badge != null && <span className="cov-section__badge">{badge}</span>}
          <FiChevronDown className={`cov-section__chevron${open ? ' cov-section__chevron--open' : ''}`} />
        </span>
      </button>
      {open && <div className="cov-section__body">{children}</div>}
    </div>
  );
}

// A stored radio value may be the option id (current form), the localized
// label (values saved before ids were used), or a boolean (fields converted
// from checkbox — their options carry the yes/no ids).
function radioValueMatches(value, opt) {
  return value === opt.id || value === opt.label
    || (value === true && opt.id === 'yes')
    || (value === false && opt.id === 'no');
}

/**
 * Resolve which schema fields are hidden by the radio visibility rules.
 * Mirrors the rule engine used by the contract form and the contract paper so
 * the read-only overview shows exactly the fields the author filled in.
 */
function computeHiddenFieldIds(fields, fieldData, sectionId) {
  const hiddenIds = new Set();
  const visibleIds = new Set();

  fields.forEach((field) => {
    if (field.type !== 'radio') return;
    const selectedValue = fieldData[`${sectionId}_${field.id}`];
    if (!selectedValue) {
      const defRule = field.defaultVisibilityRule;
      if (defRule && (defRule.fieldIds || []).length > 0) {
        if (defRule.action === 'show') defRule.fieldIds.forEach((id) => visibleIds.add(id));
        else if (defRule.action === 'hide') defRule.fieldIds.forEach((id) => hiddenIds.add(id));
      }
      return;
    }
    (field.options || []).forEach((opt) => {
      if (!radioValueMatches(selectedValue, opt)) return;
      const rule = opt.visibilityRule;
      if (!rule || (rule.fieldIds || []).length === 0) return;
      if (rule.action === 'show') rule.fieldIds.forEach((id) => visibleIds.add(id));
      else if (rule.action === 'hide') rule.fieldIds.forEach((id) => hiddenIds.add(id));
    });
  });

  const hasShowRules = fields.some((f) => {
    if (f.type !== 'radio') return false;
    const selectedValue = fieldData[`${sectionId}_${f.id}`];
    if (!selectedValue) {
      const defRule = f.defaultVisibilityRule;
      return defRule && defRule.action === 'show' && (defRule.fieldIds || []).length > 0;
    }
    return (f.options || []).some(
      (o) => radioValueMatches(selectedValue, o) && o.visibilityRule
        && o.visibilityRule.action === 'show' && (o.visibilityRule.fieldIds || []).length > 0
    );
  });

  if (hasShowRules) {
    fields.forEach((f) => {
      if (f.type === 'radio') return;
      if (!visibleIds.has(f.id)) hiddenIds.add(f.id);
    });
  }

  return hiddenIds;
}

/**
 * The contract overview rendered as the creation form in read-only mode —
 * same sections, same field order, same two-column grid, but every control is
 * a static value box instead of an input.
 *
 * Pass a full `contract` (and optionally its `fieldSchema`), or just a
 * `contractId` and the component fetches both — so every screen that shows a
 * contract renders the identical view.
 */
export default function ContractOverview({ contract: contractProp, contractId, fieldSchema: fieldSchemaProp }) {
  const { t, i18n } = useTranslation();
  // The contract stores the ISO code; the readable name comes from the
  // Currency definition and follows the active language.
  const { currencyLabel } = useCurrencyNames();

  const [fetchedContract, setFetchedContract] = useState(null);
  const [fetchedSchema, setFetchedSchema] = useState(null);
  const [loading, setLoading] = useState(!contractProp && !!contractId);

  const contract = contractProp || fetchedContract;
  const contractTypeName = getLocalized(contract?.contractTypeId?.name, i18n.language);

  // Fetch the contract when only an id was supplied (e.g. the approval inbox).
  useEffect(() => {
    if (contractProp || !contractId) return;
    setLoading(true);
    contractsAPI.get(contractId)
      .then(({ data }) => setFetchedContract(data.contract))
      .catch(() => setFetchedContract(null))
      .finally(() => setLoading(false));
  }, [contractId, contractProp]);

  // Fetch the type's field schema unless the parent already has one.
  const contractTypeId = contract?.contractTypeId?._id || contract?.contractTypeId;
  useEffect(() => {
    if (fieldSchemaProp !== undefined || !contractTypeId) return;
    import('../../services/api').then(({ parametersAPI }) => {
      parametersAPI.contractTypes.getConfig(contractTypeId)
        .then((res) => setFetchedSchema(sanitizeFieldSchema(res.data.fieldSchema) || null))
        .catch(() => setFetchedSchema(null));
    });
  }, [contractTypeId, fieldSchemaProp]);

  // Localize whatever we end up with. A schema passed in by a parent may still
  // hold multilang maps ({ en, tr }) for titles/labels, and rendering one of
  // those as a React child throws (error #31).
  const fieldSchema = useMemo(() => {
    const raw = fieldSchemaProp !== undefined ? fieldSchemaProp : fetchedSchema;
    return raw ? sanitizeFieldSchema(raw) : raw;
  }, [fieldSchemaProp, fetchedSchema]);

  if (loading) {
    return (
      <div className="cov cov--loading">
        <div className="cov__spinner" />
      </div>
    );
  }

  if (!contract) {
    if (!contractId) return null;
    return (
      <div className="cov cov--empty">
        <FiAlertCircle />
        <span>{t('contractPreviewCard.couldNotLoad', { defaultValue: 'Could not load contract' })}</span>
      </div>
    );
  }

  const fieldData = contract.fieldData || {};
  const isBlank = (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0);
  const date = (v) => (isBlank(v) ? null : formatDateMedium(v, v));
  const priority = PRIORITY_ORDER.includes(contract.priority) ? contract.priority : 'medium';
  const confidentiality = CONFIDENTIALITY_ORDER.includes(contract.confidentialityLevel)
    ? contract.confidentialityLevel
    : 'internal';

  // Format a schema field's stored value for display.
  const renderFieldValue = (field, raw) => {
    if (field.type === 'checkbox') {
      const on = raw === true || raw === 'true';
      return (
        <span className={`cov-bool${on ? ' cov-bool--on' : ''}`}>
          {on ? <FiCheck size={12} /> : <FiX size={12} />}
          {on ? t('common.yes') : t('common.no')}
        </span>
      );
    }
    if (field.type === 'radio') {
      // Values are stored by option id — show the option's localized label.
      const opt = (field.options || []).find((o) => radioValueMatches(raw, o));
      return <span className="cov-chip">{opt ? opt.label : String(raw)}</span>;
    }
    if (field.type === 'date') return formatDateMedium(raw, raw);
    if (field.type === 'currency') {
      return Number.isFinite(Number(raw)) ? formatMoney(raw) : String(raw);
    }
    if (field.type === 'number') {
      return Number.isFinite(Number(raw)) ? formatNumber(raw) : String(raw);
    }
    return String(raw);
  };

  const renderCellValue = (col, raw) => {
    if (isBlank(raw)) return <span className="cov-cell-empty">—</span>;
    if (col.type === 'checkbox') return raw === true || raw === 'true' ? t('common.yes') : t('common.no');
    if (col.type === 'date') return formatDateMedium(raw, raw);
    if (col.type === 'currency') {
      return Number.isFinite(Number(raw)) ? formatMoney(raw) : String(raw);
    }
    if (col.type === 'number') {
      return Number.isFinite(Number(raw)) ? formatNumber(raw) : String(raw);
    }
    return String(raw);
  };

  const hasCounterpartyDetails = !!(
    contract.counterpartyContactPerson || contract.counterpartyEmail || contract.counterpartyPhone
    || contract.counterpartyCountry || contract.counterpartyCity || contract.counterpartyAddress
  );

  return (
    <div className="cov">
      {/* ── Contract Identity ── */}
      <ViewSection
        title={t('contractForm.counterpartyIdentity')}
        subtitle={t('contractForm.counterpartyIdentitySubtitle')}
        icon={FiFileText}
      >
        {/* Two-column reading order: Name (full) → Company | Number →
            Type | Date → Department | Person. */}
        <div className="cov-fields">
          <ViewField label={t('contractForm.contractName')} icon={FiFileText} full isEmpty={isBlank(contract.contractName)}>
            {contract.contractName}
          </ViewField>

          <ViewField label={t('contractForm.company', { defaultValue: 'Company' })} icon={FiBriefcase} isEmpty={!contract.companyId?.name}>
            {contract.companyId?.name}
          </ViewField>

          <ViewField label={t('contractDetail.contractNumber', { defaultValue: 'Contract #' })} icon={FiHash} isEmpty={isBlank(contract.contractNumber)}>
            <span className="cov-mono">{contract.contractNumber}</span>
          </ViewField>

          <ViewField label={t('contractForm.contractType')} icon={FiLayers} isEmpty={isBlank(contractTypeName)}>
            {contractTypeName}
          </ViewField>

          <ViewField label={t('contractForm.contractDate')} icon={FiCalendar} isEmpty={isBlank(contract.contractDate)}>
            {date(contract.contractDate)}
          </ViewField>

          <ViewField label={t('contractForm.responsibleDepartment')} icon={FiBriefcase} isEmpty={isBlank(contract.responsibleDepartmentId)}>
            {contract.responsibleDepartmentId}
          </ViewField>

          <ViewField
            label={t('contractForm.responsiblePerson', { defaultValue: 'Responsible Person' })}
            icon={FiUser}
            isEmpty={!contract.responsibleUserId}
          >
            {contract.responsibleUserId
              ? `${contract.responsibleUserId.firstName || ''} ${contract.responsibleUserId.lastName || ''}`.trim()
                || contract.responsibleUserId.email
              : ''}
          </ViewField>

          <ViewField label={t('contractForm.templateSource')} icon={FiFile} isEmpty={isBlank(contract.templateSource)}>
            {contract.templateSource === 'third_party'
              ? t('contractForm.templateSources.thirdParty')
              : t('contractForm.templateSources.internal')}
          </ViewField>

          {/* Priority + Confidentiality — the form's chip selectors, locked to
              the chosen values */}
          <div className="cov-fields__row">
            <div className="cov-field">
              <label className="cov-field__label">
                <FiShield size={13} />
                {t('contractForm.priority')}
              </label>
              <div className="cov-priority">
                {PRIORITY_ORDER.map((p) => (
                  <span
                    key={p}
                    className={`cov-priority__item${p === priority ? ' cov-priority__item--active' : ''}`}
                    style={p === priority ? { '--p': PRIORITY_COLOR[p] } : undefined}
                  >
                    <span className="cov-priority__dot" style={{ background: PRIORITY_COLOR[p] }} />
                    {t(`contractForm.priorities.${p}`)}
                  </span>
                ))}
              </div>
            </div>

            <div className="cov-field">
              <label className="cov-field__label">
                <FiLock size={13} />
                {t('contractForm.confidentialityLevel')}
              </label>
              <div className="cov-priority">
                {CONFIDENTIALITY_ORDER.map((c) => (
                  <span
                    key={c}
                    className={`cov-priority__item${c === confidentiality ? ' cov-priority__item--active' : ''}`}
                    style={c === confidentiality ? { '--p': CONFIDENTIALITY_COLOR[c] } : undefined}
                  >
                    <span className="cov-priority__dot" style={{ background: CONFIDENTIALITY_COLOR[c] }} />
                    {t(`contractForm.confidentialityLevels.${c}`)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ViewSection>

      {/* ── Counterparty ── */}
      <ViewSection
        title={t('contractForm.counterpartySection')}
        subtitle={t('contractForm.counterpartySectionSubtitle')}
        icon={FiBriefcase}
        color="#8b5cf6"
      >
        <div className="cov-fields">
          <ViewField label={t('contractForm.selectCounterparty', { defaultValue: 'Counterparty' })} icon={FiBriefcase} full isEmpty={isBlank(contract.counterpartyName)}>
            {contract.counterpartyName}
          </ViewField>

          {hasCounterpartyDetails && (
            <>
              <ViewField label={t('contractForm.contactPerson')} icon={FiUser} isEmpty={isBlank(contract.counterpartyContactPerson)}>
                {contract.counterpartyContactPerson}
              </ViewField>
              <ViewField label={t('contractForm.email')} icon={FiMail} isEmpty={isBlank(contract.counterpartyEmail)}>
                {contract.counterpartyEmail}
              </ViewField>
              <ViewField label={t('contractForm.phone')} icon={FiPhone} isEmpty={isBlank(contract.counterpartyPhone)}>
                {contract.counterpartyPhone}
              </ViewField>
              <ViewField label={t('contractForm.country')} icon={FiMapPin} isEmpty={isBlank(contract.counterpartyCountry)}>
                {contract.counterpartyCountry}
              </ViewField>
              <ViewField label={t('contractForm.city')} icon={FiMapPin} isEmpty={isBlank(contract.counterpartyCity)}>
                {contract.counterpartyCity}
              </ViewField>
              <ViewField label={t('contractForm.address')} icon={FiMapPin} full isEmpty={isBlank(contract.counterpartyAddress)}>
                {contract.counterpartyAddress}
              </ViewField>
            </>
          )}
        </div>
      </ViewSection>

      {/* ── Dates & Timeline ── */}
      <ViewSection
        title={t('contractForm.datesAndTimeline')}
        subtitle={t('contractForm.datesAndTimelineSubtitle')}
        icon={FiCalendar}
        color="#10b981"
      >
        <div className="cov-fields">
          <ViewField label={t('contractForm.startDate')} icon={FiCalendar} isEmpty={isBlank(contract.startDate)}>
            {date(contract.startDate)}
          </ViewField>
          <ViewField label={t('contractForm.endDate')} icon={FiCalendar} isEmpty={isBlank(contract.endDate)}>
            {date(contract.endDate)}
          </ViewField>
          <ViewField label={t('contractForm.signingDate')} icon={FiCalendar} isEmpty={isBlank(contract.signingDate)}>
            {date(contract.signingDate)}
          </ViewField>
          <ViewField label={t('contractForm.reminderDays')} icon={FiClock} isEmpty={isBlank(contract.reminderDays)}>
            {contract.reminderDays}
          </ViewField>

          <div className="cov-field cov-field--full">
            <label className="cov-field__label">{t('contractForm.autoRenewal')}</label>
            <div className={`cov-bool${contract.autoRenewal ? ' cov-bool--on' : ''}`}>
              {contract.autoRenewal ? <FiCheck size={12} /> : <FiX size={12} />}
              {contract.autoRenewal ? t('common.yes') : t('common.no')}
            </div>
          </div>
        </div>
      </ViewSection>

      {/* ── Financial (legacy) ── Financial fields live in the Form Alanları
          Şablonu sections now; this fixed section only renders for contracts
          that still carry values on the old model fields. */}
      {(!isBlank(contract.contractAmount) || !isBlank(contract.currency) || !isBlank(contract.financialNote)) && (
        <ViewSection
          title={t('contractForm.financialDetails')}
          subtitle={t('contractForm.financialDetailsSubtitle')}
          icon={FiDollarSign}
          color="#f59e0b"
        >
          <div className="cov-fields">
            <ViewField label={t('contractForm.contractAmount')} icon={FiDollarSign} isEmpty={isBlank(contract.contractAmount)}>
              {formatMoney(contract.contractAmount, String(contract.contractAmount ?? ''))}
            </ViewField>
            <ViewField label={t('contractForm.currency')} isEmpty={isBlank(contract.currency)}>
              {currencyLabel(contract.currency)}
            </ViewField>
            <ViewField
              label={t('contractForm.explanation', { defaultValue: 'Explanation' })}
              isEmpty={isBlank(contract.financialNote)}
              full
              multiline
            >
              {contract.financialNote}
            </ViewField>
          </div>
        </ViewSection>
      )}

      {/* ── Schema-driven custom sections ── */}
      {(fieldSchema?.sections || []).map((section) => {
        // Passive sections (standard sections set inactive) don't render.
        if (section.isActive === false) return null;
        const hiddenIds = section.type === 'fields'
          ? computeHiddenFieldIds(section.fields || [], fieldData, section.id)
          : new Set();
        const rows = section.type === 'table' ? (fieldData[`table_${section.id}`] || []) : [];

        return (
          <ViewSection
            key={section.id}
            title={section.title}
            icon={FiGrid}
            color={section.color}
          >
            {section.type === 'fields' && (
              <div className="cov-fields">
                {(section.fields || []).map((field) => {
                  if (field.isActive === false) return null; // passive standard field
                  if (hiddenIds.has(field.id)) return null;
                  const raw = fieldData[`${section.id}_${field.id}`];
                  const blank = field.type === 'checkbox' ? false : isBlank(raw);
                  return (
                    <ViewField
                      key={field.id}
                      label={field.label}
                      full={['textarea', 'checkbox', 'radio'].includes(field.type)}
                      multiline={field.type === 'textarea'}
                      isEmpty={blank}
                    >
                      {renderFieldValue(field, raw)}
                    </ViewField>
                  );
                })}
              </div>
            )}

            {section.type === 'table' && (
              rows.length === 0 ? (
                <p className="cov-empty">{t('contractDetail.noRowsAdded')}</p>
              ) : (
                <div className="cov-table-wrap">
                  <table className="cov-table">
                    <thead>
                      <tr>
                        <th className="cov-table__num">#</th>
                        {(section.tableColumns || []).map((col) => <th key={col.id}>{col.header}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, ri) => (
                        <tr key={ri}>
                          <td className="cov-table__num">{ri + 1}</td>
                          {(section.tableColumns || []).map((col) => (
                            <td key={col.id}>{renderCellValue(col, row[col.id])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </ViewSection>
        );
      })}

      {/* ── Tags ── */}
      <ViewSection
        title={t('contractForm.tagsSection')}
        subtitle={t('contractForm.tagsSubtitle')}
        icon={FiTag}
        color="#8b5cf6"
        badge={(contract.tags || []).length || undefined}
      >
        {(contract.tags || []).length > 0 ? (
          <div className="cov-tags">
            {contract.tags.map((tag, i) => <span key={i} className="cov-tag">{tag}</span>)}
          </div>
        ) : (
          <p className="cov-empty">{t('contractDetail.noTags')}</p>
        )}
      </ViewSection>
    </div>
  );
}
